import React, {useRef, useState} from "react";
import i18n from "i18next";
import {DATE_TIME_FORMAT_MODEL} from "../../Apis/functions";
import {CustomStyleDatePickerPopper, CustomStyleDateTimePickerPopper} from "../Styles";
import {DATE_TIME_PICKER_MODEL} from "../CustomDateRangePicker/Model";


/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 **/
const CustomDateTimePickerApp = ({onlySelecting, type}) => {
    const elementRef = useRef(null);
    const [containerHeight, setContainerHeight] = useState(0);
    const [bodyHeight, setBodyHeight] = useState(0);
    const [isPmSelected, setIsPmSelected] = useState(false);
    /**
     * Calculates and sets the optimal height for the DateTimePicker popper
     * based on the available viewport space above and below the input element.
     *
     * The function ensures:
     * - The popper is positioned either above or below, depending on available space.
     * - The content height is reduced only when necessary.
     * - The confirm button (fixed height) always remains visible.
     *
     * Logic:
     * 1. Measure the space above and below the trigger element, subtracting the
     *    height needed for the confirm button.
     * 2. If the full content height fits below, use it.
     * 3. If it fits above, use it.
     * 4. If it doesn't fit entirely, reduce the content height proportionally
     *    based on the side with the most available space.
     */
    const calculateTop = () => {
        if (!elementRef.current) return;

        const rect = elementRef.current.getBoundingClientRect();
        let spaceBelow;
        let spaceAbove;
        let containerSize;
        let bodySize;
        let buttonHeight = 60;//Button height with name "OK"
        let headerHeight = 90;//Button height with name "November/December, etc..."

        switch (type){
            case DATE_TIME_PICKER_MODEL.DATE:
                spaceBelow = window.innerHeight - (rect.bottom + 30);
                spaceAbove = rect.top - 30;
                containerSize = 285;
                bodySize = containerSize - headerHeight;
                break;
            case DATE_TIME_PICKER_MODEL.TIME:
            case DATE_TIME_PICKER_MODEL.DATE_TIME:
                spaceBelow = window.innerHeight - (rect.bottom + buttonHeight);
                spaceAbove = rect.top - buttonHeight;
                containerSize = 282;
                bodySize = containerSize - buttonHeight;
                break;
            default:
                break;
        }

        let finalContainer;
        let finalBody;

        if (spaceBelow >= containerSize) {
            finalContainer = containerSize; // Fully fits below
            finalBody = bodySize;
        } else if (spaceAbove >= containerSize) {
            finalContainer = containerSize; // Fully fits above
            finalBody = bodySize;
        } else if (spaceBelow >= spaceAbove) {
            finalContainer = spaceBelow; // Reduce content below
            finalBody = DATE_TIME_PICKER_MODEL.DATE ? finalContainer - headerHeight : finalContainer - buttonHeight;
        } else if (spaceAbove >= spaceBelow) {
            finalContainer = spaceAbove; // Reduce content above
            finalBody = DATE_TIME_PICKER_MODEL.DATE ? finalContainer - headerHeight : finalContainer - buttonHeight;
        }

        if (finalBody < 0) {
            finalBody = undefined;
        }

        setContainerHeight(finalContainer);
        setBodyHeight(finalBody)
    };


    /**
     * Returns a date format compatible with MUI DatePicker (date-fns),
     * matching the current i18n language (dd/MM/yyyy or MM/dd/yyyy).
     *
     * @returns {string} The date format string for date-fns DatePicker.
     */
    const getDatePickerFormat = () => {
        let lang = i18n.language || 'en';
        if (lang.startsWith('sp')) lang = 'es';
        if (!['en', 'es', 'fr'].includes(lang)) lang = 'en';

        const model = DATE_TIME_FORMAT_MODEL[lang]?.slash || 'MM/DD/YYYY';
        // Convert Moment.js style (DD/MM/YYYY) to date-fns (dd/MM/yyyy)
        return model.replaceAll('D', 'd').replaceAll('Y', 'y');
    };

    const handleKeyDown = (event) => {
        if (onlySelecting === true){
            if (/\d/.test(event.key) || ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
                event.preventDefault();
            }
        }
    };

    /**
     * Returns the configuration object for the MUI DatePicker popper.
     *
     * This includes:
     * - Custom dynamic styling based on the calculated height.
     * - Popper.js modifiers to control positioning behavior (flip, overflow handling).
     *
     * @returns {Object} Popper configuration for MUI DatePicker.
     */
    const getPopperStyles = () => {
        let sx;
        switch (type){
            case DATE_TIME_PICKER_MODEL.DATE:
                sx =  CustomStyleDatePickerPopper({ containerHeight, bodyHeight });
                break;
            case DATE_TIME_PICKER_MODEL.DATE_TIME:
                sx =  CustomStyleDateTimePickerPopper({ containerHeight, bodyHeight, isPmSelected });
                break;
            default:
                break;
        }
        return {
            sx: sx,
            disablePortal: false,
            modifiers: [
                {
                    name: "flip",
                    enabled: true,
                    options: {
                        fallbackPlacements: ["top", "bottom"],
                    },
                },
                {
                    name: "preventOverflow",
                    enabled: true,
                    options: {
                        boundary: "viewport",
                        padding: 8,
                    },
                },
            ],
        }
    }

    return {
        elementRef,
        calculateTop,
        handleKeyDown,
        getPopperStyles,
        getDatePickerFormat,
        isPmSelected,
        setIsPmSelected
    };
};

export default CustomDateTimePickerApp;
